import { useQuery } from '@tanstack/react-query';

import { analyticsApi } from '@/api';
import { MetricCard } from '@/components/ui';
import { count } from '@/features/analytics/format';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { PERMISSIONS } from '@/types/roles';

const PERIOD_DAYS = 30;

/**
 * The recruitment numbers at the top of the overview: the same figures as the
 * Analytics page, read from the same endpoint, each one a way into the screen
 * where it can be acted on. Shown only to members who may read analytics.
 */
export function RecruitmentSummary() {
  const { can } = usePermissions();
  // A tile links only where this member may go.
  const link = (to, permission) => (can(permission) ? to : undefined);
  const { data, isPending, isError } = useQuery({
    queryKey: ['company-analytics-overview', PERIOD_DAYS],
    queryFn: () => analyticsApi.getCompanyOverview(PERIOD_DAYS),
  });

  // Quietly absent on failure: the overview has other things to show, and the
  // Analytics page reports the error properly.
  if (isError) return null;
  const totals = data?.totals ?? {};
  const value = (number) => (isPending ? '…' : count(number));

  return (
    <section aria-label="Recruitment summary" className="mt-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Active internships"
          icon="briefcase"
          value={value(totals.live_internships)}
          hint="Live and taking applications"
          to={link(paths.company.internships, PERMISSIONS.INTERNSHIP_READ)}
        />
        <MetricCard
          label="Applications received"
          icon="application"
          tone="info"
          value={value(totals.applications)}
          hint={`In the last ${PERIOD_DAYS} days`}
          to={link(paths.company.applications, PERMISSIONS.APPLICATION_READ)}
        />
        <MetricCard
          label="Interviews"
          icon="calendar"
          tone="warning"
          value={value(totals.interviews)}
          hint={`In the last ${PERIOD_DAYS} days`}
          to={link(paths.company.interviewCalendar, PERMISSIONS.INTERVIEW_CREATE)}
        />
        <MetricCard
          label="Hires"
          icon="check"
          tone="success"
          value={value(totals.hires)}
          hint={isPending ? 'In the last 30 days' : `${count(totals.offers)} offers made`}
          to={link(paths.company.analytics, PERMISSIONS.ANALYTICS_READ)}
        />
      </div>
    </section>
  );
}
